import { Investments } from '../../components/FinancesByFy/FinancesByFyProps';
import { getFY, getFyNumber } from '../../utils/calculation';
import {
  createEmptyFiscalYear,
  extendFiscalYears,
} from '../../components/ProjectTab/ProjectDatesFields/utils';
import { DirectiveResponse, TableProject } from '../../models/project';
import { CommentTypesEnum } from '../../models/selectOptionsModels';
import { returnTotals } from '../../components/ProjectTabFinancials/utils';
import { FinancialSummaryInfo } from '../approval/types';
import {
  formatToThousands,
  returnFormattedForecastsForFutureFys,
} from '../utils/utils';
import { returnCurrencyObject } from '../../hooks/useLocalCurrency';

export const mergeProjectsArray = (newProjects, state) => {
  const stateMap = new Map();
  state.projects.forEach((project) => {
    stateMap.set(project.id, project);
  });
  newProjects.forEach((project) => {
    stateMap.set(project.id, project);
  });
  state.projects = [...stateMap.values()];
};

export const replaceSlashes = (url: string, replacement: string): string => {
  return url.replace(/\//g, replacement);
};

export const restoreSlashes = (url: string, replacement: string): string => {
  return url?.replace(new RegExp(replacement, 'g'), '/');
};

export const trimDate = (date: Date | string) => {
  return date ? new Date(date).toISOString().split('T')[0] : date;
};

export const formatComments = (project) => {
  return {
    statusComments: getCommentTextByType(
      project?.etrCommentEntities,
      CommentTypesEnum.COUNTRY_PROJECT_STATUS_COMMENTS,
    ),
    projectTimeStatusComment: getCommentTextByType(
      project?.etrCommentEntities,
      CommentTypesEnum.PROJECT_TIME_STATUS_COMMENTS,
    ),
  };
};

const returnFormattedForecast = (
  forecast: FinancialSummaryInfo,
  currencyRate,
) => {
  return {
    forecastLandCost: formatToThousands(forecast.landCost * currencyRate),
    forecastPropertyInvestment: formatToThousands(
      forecast.propertyInvestment * currencyRate,
    ),
    forecastRetailInvestment: formatToThousands(
      forecast.retailInvestment * currencyRate,
    ),
    forecastRetailCost: formatToThousands(forecast.retailCost * currencyRate),
    totalForecast: formatToThousands(forecast.totalInvestment * currencyRate),
  };
};

export const mapFinancialSummaryInfos = (project, currencies) => {
  const currentFyForecast =
    project.financialSummaryInfos?.[0] &&
    returnFormattedForecast(
      project.financialSummaryInfos?.[0],
      returnCurrencyObject(
        currencies,
        project.countryCode,
        project.financialSummaryInfos?.[0].fiscalYear,
      )?.currencyRate,
    );
  const futureFyForecasts = returnFormattedForecastsForFutureFys(
    project,
    currencies,
  );
  return {
    ...(currentFyForecast || {}),
    ...futureFyForecasts,
  };
};

export const populateInvestmentsAndComments = (project) => {
  const object = {};
  project?.financialSummaries?.forEach((investment) => {
    if (!object[investment.fiscalYear]) {
      object[investment.fiscalYear] = createEmptyFiscalYear(
        investment.fiscalYear,
      );
    }
    const year = { ...object[investment.fiscalYear] };
    object[investment.fiscalYear] = {
      ...year,
      [investment.financialCategory]: investment,
    };
  });
  project?.investments?.forEach((investment) => {
    if (!object[investment.fiscalYear]) {
      object[investment.fiscalYear] = createEmptyFiscalYear(
        investment.fiscalYear,
      );
    }
    const year = { ...object[investment.fiscalYear] };
    object[investment.fiscalYear] = {
      ...year,
      BUDGET: investment,
    };
  });

  const investments = Object.values(object);
  const endFy = getFyNumber(getFY(0, project?.targetProjectEndDate));
  const startFy = getFyNumber(getFY(0, project?.targetProjectStartDate));
  const initialStartDateFY = getFyNumber(getFY(0, project?.projectStartDate));
  const initialOpeningDateFY = getFyNumber(
    getFY(0, project?.projectCompletionDate),
  );
  let targetStartFY;
  let targetEndFY;
  if (endFy - startFy > -1) {
    targetStartFY = startFy > initialStartDateFY ? initialStartDateFY : startFy;
    targetEndFY = endFy > initialOpeningDateFY ? endFy : initialOpeningDateFY;
  }
  const newInvestments = extendFiscalYears(
    investments as Investments[],
    targetStartFY,
    targetEndFY,
  );
  const totals = returnTotals(newInvestments);

  const comments = formatComments(project);

  return { ...project, investments: newInvestments, ...comments, ...totals };
};

export const formatTotalFinancialsSummaries = (project: TableProject) => {
  const object = {};
  Object.keys(project.financialSummaryInfo || {}).forEach((key) => {
    object[key] = project.financialSummaryInfo[key];
  });
  return object;
};

export const getCommentTextByType = (comments, commentType) => {
  const comment = comments?.find(
    (c: { commentType: string }) => c.commentType === commentType,
  );
  return comment ? comment.commentText : null;
};
const isNotNaN = (value) => (!isNaN(value) ? value : 0);

export const emptyStats = () => ({
  opex: 0,
  opexEur: 0,
  capex: 0,
  capexEur: 0,
  total: 0,
  totalEur: 0,
});
export const calculateStats = (project) =>
  project?.financialSummaryInfos?.reduce((projectStatistics, currentInfo) => {
    if (currentInfo.financialCategory !== 'FORECAST') return projectStatistics;
    return {
      opex: projectStatistics.opex + isNotNaN(currentInfo.retailCost),
      opexEur: projectStatistics.opexEur + isNotNaN(currentInfo.retailCostEur),
      capex:
        projectStatistics.capex +
        isNotNaN(currentInfo.propertyInvestment) +
        isNotNaN(currentInfo.retailInvestment),
      capexEur:
        projectStatistics.capexEur +
        isNotNaN(currentInfo.propertyInvestmentEur) +
        isNotNaN(currentInfo.retailInvestmentEur),
      total: projectStatistics.total + isNotNaN(currentInfo.totalInvestment),
      totalEur:
        projectStatistics.totalEur + isNotNaN(currentInfo.totalInvestmentEur),
    };
  }, emptyStats()) || emptyStats();

export const INBOUND_FIELD = 'inboundCapacityThroughput';

export const mapDirectiveToProjectFields = (directive: DirectiveResponse) => {
  return {
    ...directive,
    referenceId: directive.projectReferenceId,
    countryName: directive.country,
    mergedWithReferenceId: directive.dependenciesToOtherProjects,
    truckFulfilmentCapacityAdjustment: directive.truckFulfilmentCapacity,
    parcelFulfilmentCapacityAdjustment: directive.parcelFulfilmentCapacity,
    totalNumberOfFullTimeEmployees: directive.totalNumberOfFTEs,
    propertyInvestment: formatToThousands(directive.propertyInvestment),
    retailInvestment: formatToThousands(directive.retailInvestment),
    retailCost: formatToThousands(directive.retailCost),
    totalLeaseAmount: formatToThousands(directive.externalLeaseCommitment),
    totalProjectAmountInclExternalLease: formatToThousands(
      directive.totalProjectAmountInclExternalLease,
    ),
    propertyInvestmentEur: formatToThousands(directive.propertyInvestmentEur),
    retailInvestmentEur: formatToThousands(directive.retailInvestmentEur),
    retailCostEur: formatToThousands(directive.retailCostEur),
    externalLeaseCommitmentEur: formatToThousands(
      directive.externalLeaseCommitmentEur,
    ),
    totalProjectAmountInclExternalLeaseEur: formatToThousands(
      directive.totalProjectAmountInclExternalLeaseEur,
    ),
  };
};
